import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/client';

const C = {
  bg: '#0a0f1a', card: '#111827', border: '#1e2a3a', text: '#e2e8f0',
  muted: '#8892a5', accent: '#3b82f6', green: '#10b981', yellow: '#f59e0b',
  red: '#ef4444', purple: '#a855f7', rowHover: '#1a2332',
};

const s = {
  page:       { padding: 24, color: C.text, minHeight: '100vh', background: C.bg },
  header:     { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 },
  title:      { fontSize: 22, fontWeight: 700, margin: 0 },
  subtitle:   { color: C.muted, fontSize: 13, marginTop: 4 },
  section:    { background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, marginBottom: 24, overflow: 'hidden' },
  sectionHdr: { padding: '14px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  sectionTtl: { fontSize: 15, fontWeight: 600, margin: 0 },
  btnPrimary: { background: C.accent },
  btnGreen:   { background: C.green },
  btnRed:     { background: C.red },
  btnSmall:   { padding: '5px 12px', borderRadius: 5, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#fff' },
  badge:      (color) => ({ display: 'inline-block', padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, color: '#fff', background: color }),
  input:      { width: '100%', padding: '10px 12px', borderRadius: 6, border: `1px solid ${C.border}`, background: '#0d1320', color: C.text, fontSize: 13, fontFamily: '"SF Mono", "Fira Code", monospace', boxSizing: 'border-box' },
  inputLabel: { width: '100%', padding: '10px 12px', borderRadius: 6, border: `1px solid ${C.border}`, background: '#0d1320', color: C.text, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' },
  fieldGroup: { marginBottom: 14 },
  fieldLabel: { fontSize: 12, fontWeight: 600, color: C.muted, marginBottom: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  fieldHint:  { fontSize: 11, color: C.muted, marginTop: 4 },
  connCard:   { padding: 20, borderBottom: `1px solid ${C.border}` },
  connHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 },
  connLabel:  { fontSize: 14, fontWeight: 700 },
  actionRow:  { display: 'flex', gap: 10, marginTop: 14, alignItems: 'center' },
  testResult: (ok) => ({ fontSize: 12, fontWeight: 600, marginLeft: 12, color: ok ? C.green : C.red }),
  newConnBox: { padding: 20, borderTop: `1px solid ${C.border}`, background: '#0d1320' },
  errorBanner: { padding: '10px 16px', background: 'rgba(239,68,68,0.1)', border: `1px solid ${C.red}`, borderRadius: 8, color: C.red, fontSize: 13, marginBottom: 16 },
};

const PROVIDER_ICONS = { mercury: '\ud83c\udfe6', clearstreet: '\ud83d\udce1' };

/* Hardcoded provider definitions so the UI always renders */
const DEFAULT_PROVIDERS = [
  {
    provider: 'mercury',
    provider_label: 'Mercury Bank',
    expected_keys: [
      { key: 'api_token', label: 'API Token', required: true },
    ],
    connections: [],
  },
  {
    provider: 'clearstreet',
    provider_label: 'Clear Street',
    expected_keys: [
      { key: 'client_id', label: 'OAuth2 Client ID', required: true },
      { key: 'client_secret', label: 'OAuth2 Client Secret', required: true },
      { key: 'entity_id', label: 'Entity ID', required: true },
    ],
    connections: [],
  },
];

export default function Settings() {
  const [providers, setProviders] = useState(DEFAULT_PROVIDERS);
  const [loading, setLoading] = useState(true);
  const [forms, setForms] = useState({});
  const [saving, setSaving] = useState({});
  const [testResults, setTestResults] = useState({});
  const [testing, setTesting] = useState({});
  const [newConn, setNewConn] = useState({});
  const [apiError, setApiError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setApiError(null);
    try {
      const res = await api.get('/integrations/settings/');
      const serverProviders = res.data.providers || [];
      // Merge server data with defaults to ensure all providers show
      const merged = DEFAULT_PROVIDERS.map(def => {
        const fromServer = serverProviders.find(sp => sp.provider === def.provider);
        return fromServer || def;
      });
      setProviders(merged);
      const f = {};
      merged.forEach(p => {
        p.connections.forEach(c => {
          f[c.id] = {};
          c.credentials.forEach(cr => { f[c.id][cr.key] = ''; });
        });
      });
      setForms(f);
    } catch (e) {
      console.error('Failed to load settings:', e);
      const status = e.response?.status;
      if (status === 401) {
        setApiError('Authentication failed. Try logging out and back in.');
      } else {
        setApiError('Could not load saved connections. You can still add new ones below.');
      }
      // Keep DEFAULT_PROVIDERS so UI still renders
      setProviders(DEFAULT_PROVIDERS);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setField = (connId, key, value) => {
    setForms(f => ({ ...f, [connId]: { ...f[connId], [key]: value } }));
  };

  const save = async (provider, connId) => {
    const creds = forms[connId] || {};
    const toSave = {};
    Object.entries(creds).forEach(([k, v]) => { if (v) toSave[k] = v; });
    if (Object.keys(toSave).length === 0) { alert('Enter at least one credential.'); return; }

    setSaving(s => ({ ...s, [connId]: true }));
    try {
      await api.post('/integrations/settings/', {
        provider,
        connection_id: connId,
        credentials: toSave,
      });
      await load();
    } catch (e) {
      alert('Save failed: ' + (e.response?.data?.error || e.message));
    } finally { setSaving(s => ({ ...s, [connId]: false })); }
  };

  const testConn = async (connId) => {
    setTesting(t => ({ ...t, [connId]: true }));
    setTestResults(r => ({ ...r, [connId]: null }));
    try {
      const res = await api.post('/integrations/test/', { connection_id: connId });
      setTestResults(r => ({ ...r, [connId]: res.data }));
    } catch (e) {
      setTestResults(r => ({ ...r, [connId]: { connected: false, message: e.response?.data?.error || e.message } }));
    } finally { setTesting(t => ({ ...t, [connId]: false })); }
  };

  const deleteConn = async (connId, label) => {
    if (!window.confirm(`Delete connection "${label}"? This will remove all stored credentials for it.`)) return;
    try {
      await api.delete('/integrations/settings/', { data: { connection_id: connId } });
      await load();
    } catch (e) {
      alert('Delete failed: ' + (e.response?.data?.error || e.message));
    }
  };

  const addConnection = async (provider) => {
    const label = (newConn[provider] || '').trim();
    if (!label) { alert('Enter a name for this connection.'); return; }
    setSaving(s => ({ ...s, [`new_${provider}`]: true }));
    try {
      await api.post('/integrations/settings/', { provider, label, credentials: {} });
      setNewConn(n => ({ ...n, [provider]: '' }));
      await load();
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.error || e.message));
    } finally { setSaving(s => ({ ...s, [`new_${provider}`]: false })); }
  };

  if (loading) return <div style={s.page}><p style={{ color: C.muted }}>Loading settings…</p></div>;

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div>
          <h1 style={s.title}>Settings</h1>
          <div style={s.subtitle}>Manage API credentials for external integrations</div>
        </div>
      </div>

      <div style={{ fontSize: 13, color: C.muted, marginBottom: 24, lineHeight: 1.6 }}>
        Add connections below. You can have multiple connections per provider (e.g. 3 Mercury bank accounts).
        Credentials are encrypted and stored in the database.
      </div>

      {apiError && <div style={s.errorBanner}>⚠ {apiError}</div>}

      {providers.map(p => (
        <div key={p.provider} style={s.section}>
          <div style={s.sectionHdr}>
            <h2 style={s.sectionTtl}>
              {PROVIDER_ICONS[p.provider] || '\ud83d\udd17'} {p.provider_label}
            </h2>
            <span style={{ fontSize: 12, color: C.muted }}>
              {p.connections.length} connection{p.connections.length !== 1 ? 's' : ''}
            </span>
          </div>

          {/* Existing connections */}
          {p.connections.map(conn => (
            <div key={conn.id} style={s.connCard}>
              <div style={s.connHeader}>
                <div>
                  <span style={s.connLabel}>{conn.label}</span>
                  {conn.is_active && <span style={{ ...s.badge(C.green), marginLeft: 8 }}>Active</span>}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  {testResults[conn.id] && (
                    <span style={s.testResult(testResults[conn.id].connected)}>
                      {testResults[conn.id].connected ? '●' : '○'} {testResults[conn.id].message}
                    </span>
                  )}
                  <button style={{ ...s.btnSmall, ...s.btnRed }} onClick={() => deleteConn(conn.id, conn.label)}>
                    Delete
                  </button>
                </div>
              </div>

              {conn.credentials.map(cred => (
                <div key={cred.key} style={s.fieldGroup}>
                  <div style={s.fieldLabel}>
                    <span>
                      {cred.label}
                      {cred.required && <span style={{ color: C.red, marginLeft: 4 }}>*</span>}
                    </span>
                    {cred.is_set && <span style={s.badge(C.green)}>Configured</span>}
                  </div>
                  <input
                    style={s.input}
                    type="password"
                    placeholder={cred.is_set ? cred.masked_value : `Enter ${cred.label.toLowerCase()}…`}
                    value={(forms[conn.id] || {})[cred.key] || ''}
                    onChange={e => setField(conn.id, cred.key, e.target.value)}
                    autoComplete="off"
                  />
                  {cred.updated_at && (
                    <div style={s.fieldHint}>Last updated: {new Date(cred.updated_at).toLocaleString()}</div>
                  )}
                </div>
              ))}

              <div style={s.actionRow}>
                <button
                  style={{ ...s.btnSmall, ...s.btnPrimary }}
                  onClick={() => save(p.provider, conn.id)}
                  disabled={saving[conn.id]}
                >
                  {saving[conn.id] ? 'Saving…' : 'Save Credentials'}
                </button>
                <button
                  style={{ ...s.btnSmall, ...s.btnGreen }}
                  onClick={() => testConn(conn.id)}
                  disabled={testing[conn.id]}
                >
                  {testing[conn.id] ? 'Testing…' : 'Test Connection'}
                </button>
              </div>
            </div>
          ))}

          {/* Add new connection */}
          <div style={s.newConnBox}>
            <div style={{ fontSize: 12, fontWeight: 600, color: C.muted, marginBottom: 8 }}>Add Connection</div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <input
                style={{ ...s.inputLabel, flex: 1 }}
                placeholder={p.provider === 'mercury' ? 'e.g. Master Fund, GP LLC, Offshore Feeder' : 'e.g. Clear Street Primary'}
                value={newConn[p.provider] || ''}
                onChange={e => setNewConn(n => ({ ...n, [p.provider]: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && addConnection(p.provider)}
              />
              <button
                style={{ ...s.btnSmall, ...s.btnPrimary }}
                onClick={() => addConnection(p.provider)}
                disabled={saving[`new_${p.provider}`]}
              >
                {saving[`new_${p.provider}`] ? 'Adding…' : '+ Add'}
              </button>
            </div>
          </div>
        </div>
      ))}

      {/* Cleanup: invalid Mercury links on Capital Sources */}
      <InvalidMercuryLinksSection />

      {/* Help */}
      <div style={s.section}>
        <div style={s.sectionHdr}>
          <h2 style={s.sectionTtl}>💡 Where to find your API keys</h2>
        </div>
        <div style={{ padding: 20, fontSize: 13, lineHeight: 1.7, color: C.muted }}>
          <div style={{ marginBottom: 16 }}>
            <strong style={{ color: C.text }}>Mercury Bank</strong><br />
            Each Mercury organization has its own API token. Log into each Mercury account →
            Settings → API Tokens → Generate a Read-Only token. Add one connection per organization above.
          </div>
          <div>
            <strong style={{ color: C.text }}>Clear Street</strong><br />
            Log into Clear Street Studio → Settings → Developer tab to create OAuth2 credentials.
            You need your Client ID, Client Secret, and Entity ID. Your 2 account IDs will be
            auto-discovered from the API once connected.
          </div>
        </div>
      </div>
    </div>
  );
}


/* ─────────────────────────────────────────────────────────────────
 *  Invalid Mercury Links cleanup tool
 *  Finds CapitalSource rows whose source_type doesn't support a
 *  Mercury bank-feed link (retained / distribution / direct_payment)
 *  but has one set anyway — and lets you clear it inline.
 * ───────────────────────────────────────────────────────────────── */
function InvalidMercuryLinksSection() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [clearing, setClearing] = useState({});

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await api.get('/investors/capital-sources/invalid-mercury-links/');
      setRows(r.data || []);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const clearOne = async (id) => {
    if (!window.confirm('Clear the Mercury link on this Capital Source?')) return;
    setClearing(prev => ({ ...prev, [id]: true }));
    try {
      await api.post(`/investors/capital-sources/${id}/clear-mercury-link/`);
      setRows(prev => prev.filter(r => r.id !== id));
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.error || e.message));
    } finally {
      setClearing(prev => ({ ...prev, [id]: false }));
    }
  };

  return (
    <div style={s.section}>
      <div style={s.sectionHdr}>
        <h2 style={s.sectionTtl}>🧹 Capital Sources — Invalid Mercury Links</h2>
        <button onClick={load} disabled={loading}
          style={{ ...s.btnSmall, background: C.accent, opacity: loading ? 0.6 : 1 }}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>
      <div style={{ padding: 20 }}>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 14, lineHeight: 1.6 }}>
          Capital Sources of type <strong>Retained Earnings</strong>, <strong>Distribution Reinvested</strong>, or <strong>Direct Payment</strong> shouldn't carry a Mercury transaction link — those source types don't represent fund-bank cash events. Any rows below have a stale link that's eating allocation envelope from a real wire. Clear them to keep the bank-feed reconciliation honest.
        </div>
        {error && <div style={s.errorBanner}>{error}</div>}
        {!loading && rows.length === 0 ? (
          <div style={{ fontSize: 13, color: C.green, fontWeight: 600 }}>
            ✓ All clean — no invalid Mercury links found.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.border}`, color: C.muted, textAlign: 'left' }}>
                <th style={{ padding: '6px 8px' }}>Source Type</th>
                <th style={{ padding: '6px 8px' }}>Investor</th>
                <th style={{ padding: '6px 8px' }}>Deal</th>
                <th style={{ padding: '6px 8px', textAlign: 'right' }}>Amount</th>
                <th style={{ padding: '6px 8px' }}>Mercury Txn</th>
                <th style={{ padding: '6px 8px' }}>Funded</th>
                <th style={{ padding: '6px 8px' }}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                  <td style={{ padding: '6px 8px' }}>
                    <span style={s.badge(C.yellow)}>{r.source_type_display}</span>
                  </td>
                  <td style={{ padding: '6px 8px' }}>{r.lp_name || '—'}</td>
                  <td style={{ padding: '6px 8px' }}>{r.deal_name || '—'}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: '"SF Mono", monospace' }}>
                    ${Number(r.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </td>
                  <td style={{ padding: '6px 8px', fontFamily: '"SF Mono", monospace', fontSize: 11, color: C.muted }}
                      title={r.bank_feed_transaction_id}>
                    {(r.bank_feed_transaction_id || '').substring(0, 16)}…
                  </td>
                  <td style={{ padding: '6px 8px', fontSize: 11, color: C.muted }}>{r.funding_date || '—'}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                    <button onClick={() => clearOne(r.id)} disabled={clearing[r.id]}
                      style={{ ...s.btnSmall, background: C.red, opacity: clearing[r.id] ? 0.6 : 1 }}>
                      {clearing[r.id] ? 'Clearing…' : 'Clear Link'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
